import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import ExportMenu from '../../Common/ExportMenu';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { tenantHeaders } from '../../../lib/tenantSession';
import {
  Bot,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  Phone,
  PhoneMissed,
  Globe,
  Target,
  Zap,
  RefreshCw,
  Timer,
  Star
} from 'lucide-react';

const AIVoiceMetrics = ({ compact = false }) => {
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE || '';

  useEffect(() => {
    if (currentBusiness?.brandId) fetchStats();
    else setLoading(false);
  }, [currentBusiness?.brandId]);

  const fetchStats = async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      // Live Firebase ID token (auto-refreshed); cached token only as fallback (stale after ~1h → 401).
      const token = (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';
      const response = await fetch(
        `${backendUrl}/api/v1/ai-voice/business-stats/${businessId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (response.ok) {
        setStats(await response.json());
      } else {
        // Honest: a failed/forbidden fetch is NOT zero data. Null + error so the UI shows "unavailable",
        // never a full board of authoritative-looking 0 / 0% / 0:00.
        setStats(null);
        setError(t('st.voice.loadError'));
      }
    } catch (err) {
      console.error('AIVoiceMetrics fetch error:', err);
      setError(t('st.voice.loadError'));
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-500 text-sm">{t('st.voice.loading')}</span>
      </div>
    );
  }

  // Honest unavailable state: no successful response means NO numbers (not fabricated zeros).
  if (error || !stats) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded">
        {error || t('st.voice.loadError')}
      </div>
    );
  }

  const s = stats;
  const autoResolutionRate = s.total_calls > 0
    ? ((s.ai_resolved / s.total_calls) * 100).toFixed(1)
    : '0.0';
  const escalationRate = s.total_calls > 0 ? s.escalation_rate.toFixed(1) : '0.0';

  if (compact) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {t('st.voice.aiCalls')}
                </p>
                <p className="text-2xl font-bold">{s.total_calls}</p>
              </div>
              <Bot className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.aiResolved')}</p>
                <p className="text-2xl font-bold text-green-600">{autoResolutionRate}%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('st.voice.ncalls', { count: s.ai_resolved })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.escalationRate')}</p>
                <p className={`text-2xl font-bold ${parseFloat(escalationRate) > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                  {escalationRate}%
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${parseFloat(escalationRate) > 30 ? 'text-red-500' : 'text-orange-500'}`} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('st.voice.ncalls', { count: s.human_escalated })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.avgDuration')}</p>
                <p className="text-2xl font-bold text-indigo-600">{s.avg_call_duration}</p>
              </div>
              <Clock className="h-8 w-8 text-indigo-500" />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('st.voice.perCall')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.csat')}</p>
                <p className="text-2xl font-bold text-purple-600">
                  {s.voice_csat != null ? `${s.voice_csat}/5` : '—'}
                </p>
              </div>
              <Star className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {t('st.voice.totalAiCalls')}
                </p>
                <p className="text-3xl font-bold">{s.total_calls}</p>
              </div>
              <Bot className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.aiResRate')}</p>
                <p className="text-3xl font-bold text-green-600">{autoResolutionRate}%</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('st.voice.callsResolvedByAi', { count: s.ai_resolved })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.escalationRate')}</p>
                <p className={`text-3xl font-bold ${parseFloat(escalationRate) > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                  {escalationRate}%
                </p>
              </div>
              <AlertTriangle className={`h-10 w-10 ${parseFloat(escalationRate) > 30 ? 'text-red-500' : 'text-yellow-500'}`} />
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('st.voice.nEscalated', { count: s.human_escalated })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.avgCallDuration')}</p>
                <p className="text-3xl font-bold text-indigo-600">{s.avg_call_duration}</p>
              </div>
              <Clock className="h-10 w-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.csat')}</p>
                <p className="text-3xl font-bold text-purple-600">
                  {s.voice_csat != null ? `${s.voice_csat}/5` : '—'}
                </p>
              </div>
              <Star className="h-10 w-10 text-purple-500" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {s.voice_rating_count > 0 ? t('st.voice.nRatings', { count: s.voice_rating_count }) : t('st.voice.noRatings')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Callback & SLA Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.aiHandled')}</p>
                <p className="text-3xl font-bold text-blue-600">{s.ai_handled}</p>
              </div>
              <Zap className="h-10 w-10 text-blue-400" />
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('st.voice.noHumanTransfer')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.pendingCallbacks')}</p>
                <p className="text-3xl font-bold text-yellow-600">{s.pending_callbacks}</p>
              </div>
              <Phone className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.overdueCallbacks')}</p>
                <p className="text-3xl font-bold text-red-600">{s.overdue_callbacks}</p>
              </div>
              <PhoneMissed className="h-10 w-10 text-red-500" />
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('st.voice.past5minSla')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('st.voice.avgResTime')}</p>
                <p className="text-3xl font-bold text-teal-600">{s.avg_resolution_time}</p>
              </div>
              <Timer className="h-10 w-10 text-teal-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operator Performance */}
      {(s.operator_assignments.length > 0 || s.operator_resolutions.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {s.operator_assignments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  {t('st.voice.operatorAssignments')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {s.operator_assignments.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                      {/* DR-008: show the backend-enriched operator NAME, never the raw staff_id UUID. */}
                      <span className="text-sm text-gray-700 truncate flex-1">{item.operator_name || 'Unknown staff member'}</span>
                      <Badge variant="secondary">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {s.operator_resolutions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle className="h-4 w-4" />
                  {t('st.voice.operatorResolutions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {s.operator_resolutions.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                      {/* DR-008: enriched operator NAME, never the raw staff_id UUID. */}
                      <span className="text-sm text-gray-700 truncate flex-1">{item.operator_name || 'Unknown staff member'}</span>
                      <Badge variant="outline" className="text-green-700">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {s.total_calls === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Bot className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>{t('st.voice.noData')}</p>
        </div>
      )}
    </div>
  );
};

export default AIVoiceMetrics;
